import './FilterOptionListItem.scss';

import Button from '@components/Button';
import { ArrowIcon } from '@components/Icon/Arrow';
import type { FilterOptionType } from '@providers/FilterProvider';
import { FILTER_OPTION_TYPE } from '@providers/FilterProvider';
import { createClsNameMap, formatLabelFilterOption, mapModifiers, stripHtml } from '@utils';
import { useCallback, useState } from 'react';

import type { FilterOptionItemSettings } from '@/features/filter-product-list/filter/FilterOptionItem';
import {
  FilterOptionItem,
  useFilterOptionItem,
} from '@/features/filter-product-list/filter/FilterOptionItem';
import FilterOptionItemButton from '@/features/filter-product-list/filter/FilterOptionItem/Button';
import FilterOptionItemLabel from '@/features/filter-product-list/filter/FilterOptionItem/Label';

export const clsNameMap = createClsNameMap({
  modifiers: ['disabled'],
  elements: {
    sublist: createClsNameMap(),
    'collapse-btn': createClsNameMap(),
  },
})('filter-option-list-option-item');

export type FilterOptionListItemProps = {
  value: string;
  label?: string;
  docCount?: number;
  settings: FilterOptionItemSettings;
  items?: Array<ChildProps>;
  option?: FilterOptionType;
  handle?: string;
  rateCurrency?: number;
  /**
   * @description Sublist items
   */
  children?: React.ReactNode;
};

export type ChildProps = Pick<FilterOptionListItemProps, 'value' | 'docCount' | 'items' | 'handle'>;

type State = {
  isChildListExpanded: boolean;
};

const FilterOptionListItem = ({
  value,
  label,
  handle,
  docCount,
  option,
  settings,
  children,
  items,
}: FilterOptionListItemProps) => {
  const {
    isOptionItemSelected,
    handleOptionItemSelect,
    showOutOfStockOption,
    showFilterOptionCount,
  } = useFilterOptionItem({
    value,
    filterOptionId: option?.filterOptionId || '',
    items,
    handle,
    filterType: option?.filterType,
  });

  const [state, setState] = useState<State>({
    isChildListExpanded: false,
  });

  const toggleChildListExpandedState = useCallback(() => {
    setState((prevState) => ({
      isChildListExpanded: !prevState.isChildListExpanded,
    }));
  }, []);

  const filterTypeNotHaveDocCount = [FILTER_OPTION_TYPE.STOCK];

  if (option && docCount === undefined && filterTypeNotHaveDocCount.includes(option.filterType)) {
    docCount = 0;
  }

  const disabled = docCount === 0;

  if (!showOutOfStockOption && disabled) return null;

  if (!stripHtml(value)) return null;

  return (
    <>
      <FilterOptionItem className={mapModifiers(clsNameMap, { disabled })}>
        <FilterOptionItemButton
          as='button'
          isSelected={isOptionItemSelected}
          hasCheckbox={settings.selectType === 'multiple'}
          label={
            <FilterOptionItemLabel
              label={formatLabelFilterOption(label || value, option?.prefix)}
              settings={{
                displayAllValuesInUppercaseForm: settings.displayAllValuesInUppercaseForm,
              }}
            />
          }
          docCount={docCount}
          showFilterOptionCount={showFilterOptionCount}
          onClick={handleOptionItemSelect}
        />
        {!!items?.length && (
          <Button
            aria-label={`Expand/Collapse ${value}`}
            modifiers={['transparent']}
            onClick={toggleChildListExpandedState}
            className={clsNameMap.elm('collapse-btn')}
          >
            <ArrowIcon modifiers={state.isChildListExpanded ? ['down'] : ['right']} />
          </Button>
        )}
        {children && state.isChildListExpanded && (
          <ul className={clsNameMap.elm('sublist')}>{state.isChildListExpanded && children}</ul>
        )}
      </FilterOptionItem>
    </>
  );
};

export default FilterOptionListItem;
